import React, { useEffect, useState } from 'react';
import { Steps, Button, Tooltip, Layout } from 'antd';
import {
  ExclamationCircleOutlined,
  ArrowRightOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';

import Step1 from './app/steps/step1/Step1';
import { informationDefaultValues } from './app/steps/step1/YourInformation';
import { yourZipCodeDefaultValue } from './app/steps/step1/YourZipCode';
import Step2, { HRADefaultValues } from './app/steps/Step2';
import Step3Form, { Step3DefaultValues } from './app/steps/step3/Step3Form';
import GetStartedPage from './app/welcome/Welcome';
import { getAge } from './app/steps/helper';

import './App.less';
import './app/steps/Steps.less';

import {
  State,
  Information,
  Step3,
  StepContent,
  HRAInformation,
  ZipCode,
} from './types';
import FinishPage from './app/FinishPage';

const { Content } = Layout;

const DEFAULT_ACTIVE_KEY = 1;
const DEFAULT_DISABLED_KEY = 0;

const App: React.FC = () => {
  const { Step } = Steps;

  const [currentStep, setCurrentStep] = useState(-1);
  const [enableNext, setEnableNext] = useState(false);
  const [enableDoneButton, setEnableDoneButton] = useState(false);
  const [state, setState] = useState<State | null>(null);
  const [step2Subtitle, setStep2Subtitle] = useState('');
  const [step3Subtitle, setStep3Subtitle] = useState('');
  const [step1Subtitle, setStep1Subtitle] = useState('');
  const [lowestCostPlan, setLowestCostPlan] = useState(0);
  const [showGetStarted, setShowGetStarted] = useState(true);
  const [showAffordableResult, setShowAffordableResult] = useState(false);
  const [showUnaffordableResult, setShowUnaffordableResult] = useState(false);
  const [showRecommendation, setShowRecommendation] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState(DEFAULT_ACTIVE_KEY);
  const [confettiShown, setConfettiShown] = useState<boolean>(false);

  const steps: StepContent[] = [
    {
      title: 'Your information',
      description: step1Subtitle,
    },
    {
      title: (
        <div>
          Is your allowance considered affordable?
          <Tooltip
            title="The IRS defines affordable health plans as costing less than 9.96% of your income."
            className="ml-8"
          >
            <ExclamationCircleOutlined />
          </Tooltip>
        </div>
      ),
      description: step2Subtitle,
    },
    {
      title: 'Should you opt-in or opt-out?',
      description: step3Subtitle,
    },
  ];
  const handleCollapseChange = () => {
    setActiveAccordion(
      activeAccordion === DEFAULT_ACTIVE_KEY
        ? DEFAULT_DISABLED_KEY
        : DEFAULT_ACTIVE_KEY
    );
  };
  const onSetLowestCostPlan = (value: number) => {
    setLowestCostPlan(value);
  };

  const onCurrentUpdated = (value: number) => {
    setCurrentStep(currentStep + value);
  };

  const displayAffordabilityResult = (answer: boolean) => {
    setShowAffordableResult(answer);
    setShowUnaffordableResult(!answer);
    setStep2Subtitle(answer ? 'Yes' : 'No');
  };

  const onCalulcateRecommendation = (answer: string) => {
    setShowRecommendation(true);
    setStep3Subtitle(answer);
    setEnableDoneButton(true);
  };

  const onSubmitZipCode = (values: ZipCode) => {
    if (values.zipCode !== state?.step1ZipCode.zipCode) {
      clearStep2Result();
      clearStep3Result();
    }
    setState({
      step1YourInformation: {
        ...(state?.step1YourInformation ?? informationDefaultValues),
        zipCode: values.zipCode,
      },
      step2: state?.step2 ?? HRADefaultValues,
      step3: state?.step3 ?? Step3DefaultValues,
      step1ZipCode: values,
    });
  };

  const onYourInformationComplete = (values: Information) => {
    setStep1Subtitle(
      'ZIP Code: ' +
        values.zipCode +
        ', Age: ' +
        getAge(new Date(values.birthdate ?? 0))
    );

    if (state?.step1YourInformation !== values) {
      clearStep2Result();
    }

    setState({
      step1YourInformation: values,
      step2: state?.step2 ?? HRADefaultValues,
      step3: state?.step3 ?? Step3DefaultValues,
      step1ZipCode: state?.step1ZipCode ?? yourZipCodeDefaultValue,
    });

    setActiveAccordion(DEFAULT_ACTIVE_KEY);
  };

  const onStep2YourHRAComplete = (values: HRAInformation) => {
    setState({
      step1YourInformation:
        state?.step1YourInformation ?? informationDefaultValues,
      step2: values,
      step3: state?.step3 ?? Step3DefaultValues,
      step1ZipCode: state?.step1ZipCode ?? yourZipCodeDefaultValue,
    });

    setActiveAccordion(DEFAULT_DISABLED_KEY);
    setEnableNext(true);
  };

  const onStep3Complete = (values: Step3) => {
    setState({
      step1YourInformation:
        state?.step1YourInformation ?? informationDefaultValues,
      step2: state?.step2 ?? HRADefaultValues,
      step1ZipCode: state?.step1ZipCode ?? yourZipCodeDefaultValue,
      step3: values,
    });
    setActiveAccordion(DEFAULT_DISABLED_KEY);
  };

  const next = () => {
    onCurrentUpdated(1);
    setActiveAccordion(DEFAULT_ACTIVE_KEY);
  };

  const prev = () => {
    onCurrentUpdated(-1);
  };

  const clearStep2Result = () => {
    setShowAffordableResult(false);
    setShowUnaffordableResult(false);
    setEnableNext(false);
  };
  const clearStep3Result = () => {
    setShowRecommendation(false);
    setEnableDoneButton(false);
  };
  const onGetStarted = () => {
    next();
    setShowGetStarted(false);
  };
  return (
    <div>
      {showGetStarted && (
        <GetStartedPage onSubmit={onGetStarted}></GetStartedPage>
      )}
      {!showGetStarted && !confettiShown && (
        <Layout>
          <Content className="content">
            <Steps className="m-8" current={currentStep} direction="horizontal">
              {steps.map((item, index) => (
                <Step
                  key={index}
                  title={item.title}
                  description={item.description}
                />
              ))}
            </Steps>
            <div className="steps-content">
              <Step1
                yourInfoInitialValues={state?.step1YourInformation ?? null}
                onYourInformationComplete={onYourInformationComplete}
                onSubmitZipCode={onSubmitZipCode}
                currentStep={currentStep}
                yourZipCodeInitialValues={state?.step1ZipCode ?? null}
                onCurrentUpdated={onCurrentUpdated}
                setLowestCostPlan={onSetLowestCostPlan}
              />
              <Step2
                activeAccordion={activeAccordion}
                defaultKey={DEFAULT_ACTIVE_KEY}
                handleCollapse={handleCollapseChange}
                clearStep={() => {
                  clearStep2Result();
                  clearStep3Result();
                }}
                showAffordable={showAffordableResult}
                showUnaffordable={showUnaffordableResult}
                currentStep={currentStep}
                hraInfoInitialvalues={state?.step2 ?? null}
                lowestCostPlan={lowestCostPlan}
                displayResult={displayAffordabilityResult}
                onStep2Complete={onStep2YourHRAComplete}
                householdIncome={
                  state?.step1YourInformation.householdIncome ?? null
                }
              />
              <Step3Form
                activeAccordion={activeAccordion}
                defaultKey={DEFAULT_ACTIVE_KEY}
                handleCollapse={handleCollapseChange}
                clearStep={clearStep3Result}
                showRecommendation={showRecommendation}
                affordable={step2Subtitle === 'Yes' ? true : false}
                current={currentStep}
                step3InitialValues={state?.step3 ?? null}
                onStep3Complete={onStep3Complete}
                onCalulcateRecommendation={onCalulcateRecommendation}
              ></Step3Form>
            </div>
            <div className="space-between mt-16">
              {currentStep > 0 && (
                <Button className="button__large" onClick={() => prev()}>
                  <ArrowLeftOutlined />
                  Previous
                </Button>
              )}
              {currentStep === 1 && (
                <Button
                  className="button__large "
                  htmlType="submit"
                  type="primary"
                  onClick={next}
                  disabled={!enableNext}
                >
                  Next
                  <ArrowRightOutlined />
                </Button>
              )}
              {currentStep === 2 && (
                <Button
                  onClick={() => {
                    setConfettiShown(true);
                  }}
                  className="button__large"
                  disabled={!enableDoneButton}
                  htmlType="submit"
                  type="primary"
                >
                  Finish
                </Button>
              )}
            </div>
          </Content>
        </Layout>
      )}
      {confettiShown && <FinishPage />}
    </div>
  );
};

export default App;
